import { httpClient } from "@/shared/http/httpClient";
import { OperationError } from "@/shared/errors/auth-errors";
import {
    frontendErrorsContract,
    FrontendErrorsEvents,
    FrontendErrorsEventsPageParams,
    FrontendErrorsGroups,
    FrontendErrorsGroupsParams,
    FrontendErrorsPopulationParams,
    FrontendErrorsQueryParams,
    FrontendErrorsSeries,
    FrontendErrorsSummary,
} from "@/features/observability/types/frontend-errors.types";

const API_PATH = "/api/v1/observability/frontend-errors";

export class FrontendErrorsContractError extends OperationError {}

const baseQuery = (params: FrontendErrorsQueryParams): URLSearchParams => {
    const search = new URLSearchParams();
    search.set("workspaceId", params.workspaceId);
    search.set("source", params.source);
    search.set("from", String(params.from));
    search.set("to", String(params.to));
    if (params.category !== null) search.set("category", params.category);
    if (params.errorCode !== null) search.set("errorCode", params.errorCode);
    if (params.release !== null) search.set("release", params.release);
    return search;
};

const populationQuery = (params: FrontendErrorsPopulationParams): URLSearchParams => {
    const search = baseQuery(params);
    search.set("asOf", String(params.asOf));
    return search;
};

const eventsQuery = (params: FrontendErrorsEventsPageParams): URLSearchParams => {
    const search = populationQuery(params);
    if (params.limit !== null) search.set("limit", String(params.limit));
    if (params.fingerprint !== null) search.set("fingerprint", params.fingerprint);
    if (params.cursor !== null) search.set("cursor", params.cursor);
    return search;
};

const fetchValidated = async <W, T>(
    resource: string,
    search: URLSearchParams,
    guard: (value: unknown) => value is W,
    normalize: (value: W) => T,
    signal: AbortSignal,
): Promise<T> => {
    const body = await httpClient.getJson<unknown>(`${API_PATH}/${resource}?${search.toString()}`, undefined, signal);
    if (!guard(body)) {
        throw new FrontendErrorsContractError(`The frontend errors ${resource} response does not match the contract`);
    }
    return normalize(body);
};

const getSummary = (params: FrontendErrorsQueryParams, signal: AbortSignal): Promise<FrontendErrorsSummary> =>
    fetchValidated("summary", baseQuery(params), frontendErrorsContract.isSummary, frontendErrorsContract.toSummary, signal);

const getSeries = (params: FrontendErrorsPopulationParams, signal: AbortSignal): Promise<FrontendErrorsSeries> =>
    fetchValidated("series", populationQuery(params), frontendErrorsContract.isSeries, frontendErrorsContract.toSeries, signal);

const groupsQuery = (params: FrontendErrorsGroupsParams): URLSearchParams => {
    const search = populationQuery(params);
    if (params.includeSample) search.set("includeSample", "true");
    return search;
};

const getGroups = (params: FrontendErrorsGroupsParams, signal: AbortSignal): Promise<FrontendErrorsGroups> =>
    fetchValidated("groups", groupsQuery(params), frontendErrorsContract.isGroups, frontendErrorsContract.toGroups, signal);

const getEvents = (params: FrontendErrorsEventsPageParams, signal: AbortSignal): Promise<FrontendErrorsEvents> =>
    fetchValidated("events", eventsQuery(params), frontendErrorsContract.isEvents, frontendErrorsContract.toEvents, signal);

export const frontendErrorsApi = {
    getSummary,
    getSeries,
    getGroups,
    getEvents,
};
